import { apiRequest } from './client'

export type ScenarioType = 'static' | 'sequence' | 'conditional' | 'stateful_collection'

export interface StaticConfig {
  response_id: string
}

export interface Scenario {
  id: string
  endpoint_id: string
  name: string
  type: ScenarioType
  priority: number
  enabled: boolean
  config: StaticConfig | Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface SequenceStep {
  id: string
  match_count: number | string
  response_id: string
}

export interface SequenceConfig {
  steps: SequenceStep[]
  scope: string
  reset_after_seconds?: number
}

export interface ConditionalRule {
  if: unknown
  then: { response_id: string }
}

export interface ConditionalConfig {
  rules: ConditionalRule[]
  default?: { response_id: string }
}

export type HttpActionMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export interface StatefulActionDef {
  method: HttpActionMethod
  path_match: string
  response_status?: number
  not_found_status?: number
}

export interface StatefulCollectionConfig {
  state_key: string
  id_field?: string
  id_strategy?: string
  actions: {
    create?: StatefulActionDef
    list?: StatefulActionDef
    get?: StatefulActionDef
    update?: StatefulActionDef
    delete?: StatefulActionDef
  }
  max_items?: number
  ttl_seconds?: number
}

export type CreateScenarioInput =
  | { name: string; priority?: number; enabled?: boolean; type: 'static'; config: StaticConfig }
  | { name: string; priority?: number; enabled?: boolean; type: 'sequence'; config: SequenceConfig }
  | { name: string; priority?: number; enabled?: boolean; type: 'conditional'; config: ConditionalConfig }
  | { name: string; priority?: number; enabled?: boolean; type: 'stateful_collection'; config: StatefulCollectionConfig }

export interface UpdateScenarioInput {
  name?: string
  priority?: number
  enabled?: boolean
  config?: StaticConfig | Record<string, unknown>
}

export function listScenarios(endpointId: string): Promise<Scenario[]> {
  return apiRequest<Scenario[]>(`/api/endpoints/${endpointId}/scenarios`)
}

export function createScenario(endpointId: string, input: CreateScenarioInput): Promise<Scenario> {
  return apiRequest<Scenario>(`/api/endpoints/${endpointId}/scenarios`, {
    method: 'POST',
    body: input,
  })
}

export function updateScenario(id: string, input: UpdateScenarioInput): Promise<Scenario> {
  return apiRequest<Scenario>(`/api/scenarios/${id}`, {
    method: 'PATCH',
    body: input,
  })
}

export function deleteScenario(id: string): Promise<void> {
  return apiRequest<void>(`/api/scenarios/${id}`, { method: 'DELETE' })
}

export interface ScenarioState {
  counts: Record<string, number>
}

export function getScenarioState(id: string): Promise<ScenarioState> {
  return apiRequest<ScenarioState>(`/api/scenarios/${id}/state`)
}

export function resetScenarioState(id: string): Promise<void> {
  return apiRequest<void>(`/api/scenarios/${id}/state`, { method: 'DELETE' })
}
